import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AuthStatus } from '@/enums';
import { Avatar } from '@/components/avatar';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { EMPLOYEE_ROLE_TRANSLATION_MAP } from '@/components/restaurant/restaurant-employee-list';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { isWeb } from '@/utils/navigation';
import { Linking, Text, View } from 'react-native';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { partnerSupplier } from '@/utils/environment';
import { useMaybeUserContext } from '@/contexts/user-context';
import { usePricingExperienceNavigation } from '@/hooks/use-pricing-experience-navigation';
import { useSignOut } from '@/hooks/use-sign-out';

interface Props {
  navigateToProfile: () => void;
  onClose: () => void;
  popoverOffset: number;
  targetRef: React.RefObject<View | null>;
}

type ActionListItem = ListOption<{
  action: () => Promise<void>;
}>;

export const AvatarMenuActionSheet: React.FC<Props> = ({
  navigateToProfile,
  onClose,
  popoverOffset,
  targetRef,
}) => {
  const signOut = useSignOut();
  const userContext = useMaybeUserContext();
  const { canNavigate: canNavigateToPricing, navigateToPricing } =
    usePricingExperienceNavigation();

  const [styles] = useStyles();

  const [loadingOption, setLoadingOption] = useState<ActionListItem | null>(
    null,
  );
  const actions = useMemo<ActionListItem[]>(() => {
    const actionList: ActionListItem[] = [];
    const oldWebLink = partnerSupplier.oldWebLink;

    if (canNavigateToPricing) {
      actionList.push({
        metadata: {
          action: async () => {
            await navigateToPricing();
          },
        },
        title: i18n.t('pricing.navigation.label'),
        value: i18n.t('pricing.navigation.label'),
      });
    }
    if (isWeb && oldWebLink) {
      actionList.push({
        metadata: {
          action: async () => {
            Analytics.track('old_url_link.press', {
              location: 'side_navigation',
            });
            Linking.openURL(oldWebLink);
          },
        },
        title: i18n.t('switch_to_old_version'),
        value: i18n.t('switch_to_old_version'),
      });
    }
    actionList.push({
      metadata: {
        action: async () => {
          await signOut();
        },
      },
      title: i18n.t('log_out'),
      value: i18n.t('log_out'),
    });

    return actionList;
  }, [canNavigateToPricing, signOut, navigateToPricing]);

  if (!userContext || userContext.authStatus !== AuthStatus.Authenticated) {
    return null;
  }
  const {
    user,
    selectedRestaurantRelationship: { employeeRole },
  } = userContext;
  const roleText = employeeRole
    ? EMPLOYEE_ROLE_TRANSLATION_MAP[employeeRole]
    : null;

  return (
    <BaseActionSheet
      contentContainerStyle={styles.base}
      isVisible
      onClose={() => onClose()}
      popoverStyle={(contentPosition, targetPosition) => {
        return {
          left: targetPosition.width + 8,
          // reset the default 240 min width
          minWidth: 1,
          top:
            targetPosition.y +
            targetPosition.height -
            contentPosition.height -
            popoverOffset,
        };
      }}
      popoverTarget={targetRef?.current}
    >
      <List<ActionListItem>
        contentContainerStyle={styles.listContainer}
        data={actions}
        ItemSeparatorComponent={null}
        ListHeaderComponent={
          <View style={styles.profile}>
            <Avatar
              containerStyle={styles.avatarContainer}
              name={user.name}
              size="medium"
              uuid={user.uuid}
            />
            <Text numberOfLines={2} style={[TYPOGRAPHY.small, styles.userName]}>
              {user.name}
            </Text>
            {roleText ? (
              <Text style={[TYPOGRAPHY.xsmall, styles.userRole]}>
                {roleText}
              </Text>
            ) : null}
            <Button
              onPress={() => {
                navigateToProfile();
                onClose();
              }}
              size="small"
              textStyle={[TYPOGRAPHY.small, styles.profileText]}
              trackingData={null}
              type="tertiary"
            >
              {i18n.t('view_profile')}
            </Button>
          </View>
        }
        listItemStyle={styles.listItem}
        onSelect={async (option) => {
          const metadata = option.metadata!;
          setLoadingOption(option);
          await metadata.action();
          setLoadingOption(null);
          onClose();
        }}
        renderMainContent={(option) => {
          return (
            <>
              <Text style={[TYPOGRAPHY.small, FONTS.primaryBold]}>
                {option.title}
              </Text>
              <View
                style={[
                  styles.loading,
                  option === loadingOption ? styles.loadingVisible : null,
                ]}
              >
                <ActivityIndicator />
              </View>
            </>
          );
        }}
      />
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  avatarContainer: {
    marginBottom: 8,
  },
  base: {
    paddingHorizontal: 0,
    paddingVertical: 8,
  },
  listContainer: {
    maxWidth: 244,
    minWidth: 168,
  },
  listItem: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  loading: {
    marginLeft: 8,
    opacity: 0,
  },
  loadingVisible: {
    opacity: 1,
  },
  profile: {
    alignItems: 'center',
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  profileText: {
    ...FONTS.primaryBold,
    color: theme.colors.darkGreen,
  },
  userName: {
    ...FONTS.primaryBold,
    textAlign: 'center',
  },
  userRole: {
    color: theme.colors.inactiveText,
  },
}));
